import React from 'react';
import { Box, Typography } from '@mui/material';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import CustomButton from 'lib/designSystem/Button';
import { useWarranties } from './useWarranties';
import NewQuotationDialog from './NewQuotationDialog';
import WarrantyDetailDialog from './WarrantyDetailDialog';
import {
  deleteSGRWarrantyRequest,
  updateSGRWarrantyRequest,
} from 'lib/api/sgr';
import { useDispatch } from 'react-redux';
import { CURRENCIES, SGR_TYPES } from 'lib/consts';
import { showAlert } from 'lib/functions/utils';

const Warranties = () => {
  const dispatch = useDispatch();

  const {
    warranties,
    isLoading,
    setIsLoading,
    openNewQuotation,
    fetchWarranties,
    handleOpenNewQuotation,
    handleCloseNewQuotation,
    newQuotation,
    updateNewQuotation,
    rawAmount,
    updateRawAmount,
    daysDifference,
    rutError,
    dateError,
    isFormValid,
    formattedAmount,
    formattedConvertedAmount,
    formattedConversionRate,
    createQuotation,
    pagination,
    setPagination,
  } = useWarranties();

  const [openWarrantyDetail, setOpenWarrantyDetail] = React.useState(false);
  const [selectedWarranty, setSelectedWarranty] = React.useState<any>(null);

  const handleDeleteWarranty = async () => {
    await deleteSGRWarrantyRequest(dispatch, selectedWarranty?.id);
    setOpenWarrantyDetail(false);
    fetchWarranties();
  };

  const handleRequestWarranty = async () => {
    try {
      await updateSGRWarrantyRequest(dispatch, selectedWarranty?.id, {
        status: 'IN_REVIEW',
        runDecisionEngine: true,
      });
      setOpenWarrantyDetail(false);
      fetchWarranties();
    } catch (error: any) {
      const message = error.response.data.message
        ? error.response.data.message
        : 'Error al actualizar la garantía';
      showAlert(dispatch, 'error', message);
      console.error(error);
    }
  };

  const handleOpenWarrantyDetail = (warrantyId: number) => {
    const warranty = warranties.find((w: any) => w.id === warrantyId);
    if (warranty) {
      setSelectedWarranty(warranty);
      setOpenWarrantyDetail(true);
    }
  };

  const handleCloseWarrantyDetail = () => {
    setOpenWarrantyDetail(false);
    setSelectedWarranty(null);
  };

  const tableHeaders = [
    {
      label: 'ID',
      key: 'id',
      type: 'numeric-id',
    },
    {
      label: 'Tipo',
      key: 'translatedType',
    },
    {
      label: 'Estado',
      key: 'translatedStatus',
      type: 'status',
    },
    {
      label: 'Monto Cotizado',
      key: 'quotedAmount',
      type: 'money',
    },
    {
      label: 'Moneda',
      key: 'quotedCurrency',
    },
    {
      label: 'Fecha Cotización',
      key: 'quoteDate',
      type: 'date',
    },
    {
      label: 'Fecha Inicio',
      key: 'startDate',
      type: 'date',
    },
    {
      label: 'Fecha Término',
      key: 'endDate',
      type: 'date',
    },
  ];

  if (isLoading) return <CenteredCircularProgress />;

  return (
    <>
      {warranties.length !== 0 ? (
        <TableWithPagination
          data={warranties}
          setData={fetchWarranties}
          dataHeaders={tableHeaders}
          buttons={[
            {
              name: 'Nueva Cotización',
              color: 'primary',
              action: handleOpenNewQuotation,
            },
          ]}
          filters={[
            {
              key: 'type',
              label: 'Tipo',
              options: SGR_TYPES.map((type) => type.value),
              type: 'select',
            },
          ]}
          caption="Lista de garantías emitidas y cotizaciones realizadas"
          handleClickRow={handleOpenWarrantyDetail}
          showActions
          pagination={pagination}
          setPagination={setPagination}
          searchData={fetchWarranties}
          setLoading={setIsLoading}
          exportToFile="xlsx"
          headerBackgroundColor="transparent"
          lastElement
        />
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
          flexDirection="column"
          gap={2}
        >
          <Typography variant="h4" gutterBottom color="text.primary">
            No tienes ninguna garantía emitida
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Realiza tu primera cotización para emitir tu primera garantía
          </Typography>
          <CustomButton
            color="primary"
            onClick={handleOpenNewQuotation}
            size="large"
          >
            Realizar Primera Cotización
          </CustomButton>
        </Box>
      )}

      <NewQuotationDialog
        open={openNewQuotation}
        onClose={handleCloseNewQuotation}
        onSubmit={createQuotation}
        newQuotation={newQuotation}
        updateNewQuotation={updateNewQuotation}
        rawAmount={rawAmount}
        updateRawAmount={updateRawAmount}
        daysDifference={daysDifference}
        rutError={rutError}
        dateError={dateError}
        isFormValid={isFormValid}
        CURRENCIES={CURRENCIES}
        formattedAmount={formattedAmount}
        formattedConvertedAmount={formattedConvertedAmount}
        formattedConversionRate={formattedConversionRate}
      />

      <WarrantyDetailDialog
        open={openWarrantyDetail}
        onClose={handleCloseWarrantyDetail}
        warranty={selectedWarranty}
        onDelete={handleDeleteWarranty}
        onRequest={handleRequestWarranty}
      />
    </>
  );
};

export default Warranties;
